import React, {
  ChangeEvent,
  InputHTMLAttributes,
  useState,
  useRef,
  forwardRef,
} from 'react'
import type { Ref } from 'react'
import clsx from 'clsx'
import { isEmpty, noop } from 'lodash'

import type { OptionProps } from 'types'
import { useOnClickOutside } from 'helpers/hooks'
import { getLabel } from 'helpers/language'
import { Loader } from 'components'

import TextInput from '../TextInput'
import { IconSearch } from '../Icons'

export interface TextInputSearchProps
  extends InputHTMLAttributes<HTMLInputElement> {
  isError?: boolean
  message?: string
  isLoading?: boolean
  showIcon?: boolean
  options?: OptionProps[]
  selected: OptionProps | null
  onSelected?: (key: OptionProps) => void
  fullWidth?: boolean
  withIcon?: React.ReactNode
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  value?: string
}

/**
 * Primary UI component for user interaction
 */
function TextInputSearch(
  props: TextInputSearchProps,
  ref: Ref<HTMLInputElement>,
): JSX.Element {
  const {
    className,
    isError = false,
    isLoading = false,
    disabled = false,
    showIcon = true,
    onSelected = noop,
    selected: _selected,
    fullWidth = false,
    options = [],
    message,
    withIcon,
    ...others
  } = props

  const hasIcon = typeof withIcon === 'object' && !isEmpty(withIcon)
  const divRef = useRef<HTMLDivElement>(null)
  const [selected, setSelected] = useState<OptionProps | null>(_selected)
  const [show, setShow] = useState<boolean>(false)

  const _handleSelected = (item: OptionProps) => {
    onSelected(item)
    setSelected(item)
  }

  const _handleClickOutside = () => {
    setShow(false)
  }

  useOnClickOutside(divRef, _handleClickOutside)

  const cn = clsx(
    'text-input__search',
    fullWidth && 'text-input__search--full-width',
    className,
  )

  let icon = hasIcon ? withIcon : null

  if (!icon) {
    icon = showIcon ? <IconSearch variants='sm' /> : null
  }

  const textInputProps = {
    ...others,
    showIcon: !isError,
    isError,
    className: cn,
    ref,
  }

  return (
    <div
      className={cn}
      onClick={() => (!disabled ? setShow(!show) : noop)}
      ref={divRef}
      data-testid='text-input-search'
    >
      <TextInput
        {...textInputProps}
        message={message}
        autoCapitalize='none'
        autoComplete='off'
        autoCorrect='off'
        spellCheck='false'
        type='text'
        aria-autocomplete='list'
        aria-labelledby='aria-label'
        withIcon={icon}
        disabled={disabled}
        showDropdown={show}
        fullWidth
      />

      {/* loading section */}
      {show && isLoading && (
        <ul className='text-input__search__options'>
          <li className='text-input__search--loading'>
            <Loader />
          </li>
        </ul>
      )}

      {/* list section */}
      {show && options.length > 0 && (
        <ul
          className='text-input__search__options'
          data-testid='text-input-search-list'
        >
          {options.map((item, index) => (
            <li
              key={`${item.key}-${index}`}
              className={clsx(
                'text-input__search__option',
                selected?.key === item.key &&
                  'text-input__search__option--selected',
              )}
              onClick={() => _handleSelected(item)}
              data-testid={`text-input-search-option-${index}`}
            >
              {getLabel(item.label)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default forwardRef(TextInputSearch)
